import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import AppSidebar from '../components/AppSidebar';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Share2, Copy, ExternalLink, Check, Twitter, Linkedin } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_e) {
    return false;
  }
};

const LinkRow = ({ label, url, enabled, onToggle, testIdBase }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="py-4 first:pt-0 last:pb-0" data-testid={`${testIdBase}-row`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="min-w-0">
          <Label className="text-sm font-medium">{label}</Label>
          <p className="text-xs text-muted-foreground">
            {enabled ? 'Live — anyone with the link can view.' : 'Currently private. Toggle on to publish.'}
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${testIdBase}-toggle`} />
      </div>

      {enabled && (
        <div className="flex items-center gap-2 mt-2">
          <code className="flex-1 min-w-0 text-xs bg-muted rounded-md px-3 py-2 truncate font-mono" data-testid={`${testIdBase}-url`}>
            {url}
          </code>
          <Button variant="outline" size="sm" onClick={handleCopy} data-testid={`${testIdBase}-copy`}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(url, '_blank')}
            data-testid={`${testIdBase}-visit`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const SharePage = () => {
  const { currentWorkspace, refreshUsage } = useWorkspace();
  const [roadmapPublic, setRoadmapPublic] = useState(false);
  const [changelogPublic, setChangelogPublic] = useState(false);

  useEffect(() => {
    if (!currentWorkspace) return;
    setRoadmapPublic(!!currentWorkspace.public_roadmap);
    setChangelogPublic(!!currentWorkspace.public_changelog);
  }, [currentWorkspace]);

  const updateField = async (field, value, setter) => {
    if (!currentWorkspace) return;
    setter(value);
    try {
      await axios.put(`${API_URL}/api/workspaces/${currentWorkspace.id}`, { [field]: value });
      toast.success(value ? 'Now public' : 'Now private');
      if (typeof refreshUsage === 'function') refreshUsage();
    } catch (_e) {
      setter(!value);
      toast.error('Failed to update');
    }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const slug = currentWorkspace?.slug || 'workspace';
  const roadmapUrl = `${origin}/r/${slug}`;
  const changelogUrl = `${origin}/c/${slug}`;

  const share = (platform) => {
    const text = encodeURIComponent(
      `We're building in public with VibeSprint — come see what we're shipping next:`,
    );
    const encoded = encodeURIComponent(roadmapUrl);
    let url = '';
    if (platform === 'twitter') url = `https://twitter.com/intent/tweet?text=${text}&url=${encoded}`;
    if (platform === 'linkedin') url = `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`;
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="share-page">
      <AppSidebar />
      <main className="flex-1 min-w-0">
        <div className="container mx-auto max-w-3xl px-4 sm:px-8 py-8">
          <header className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="rounded-md bg-primary/10 p-2">
                <Share2 className="h-5 w-5 text-primary" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Share & Public URLs</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Publish your roadmap and changelog at shareable URLs — great for building in public.
            </p>
          </header>

          <Card className="p-6 divide-y" data-testid="share-links-card">
            <LinkRow
              label="Public Roadmap"
              url={roadmapUrl}
              enabled={roadmapPublic}
              onToggle={(v) => updateField('public_roadmap', v, setRoadmapPublic)}
              testIdBase="share-roadmap"
            />
            <LinkRow
              label="Public Changelog"
              url={changelogUrl}
              enabled={changelogPublic}
              onToggle={(v) => updateField('public_changelog', v, setChangelogPublic)}
              testIdBase="share-changelog"
            />
          </Card>

          {(roadmapPublic || changelogPublic) && (
            <Card className="p-6 mt-6" data-testid="share-social-card">
              <h3 className="text-sm font-semibold mb-1">Announce it</h3>
              <p className="text-xs text-muted-foreground mb-4">
                One-click share to social — pre-fills a founder-friendly message.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => share('twitter')} data-testid="share-social-twitter">
                  <Twitter className="h-4 w-4 mr-1.5" />
                  Post to X / Twitter
                </Button>
                <Button variant="outline" size="sm" onClick={() => share('linkedin')} data-testid="share-social-linkedin">
                  <Linkedin className="h-4 w-4 mr-1.5" />
                  Share on LinkedIn
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default SharePage;
